import React, { useState, useRef, useCallback } from "react";
import { UserPlus, User, Building2, FileText, Tag, Mail, Phone, Loader2, TriangleAlert, AlertCircle } from "lucide-react";
import PremiumModal from "./PremiumModal";
import SmsOptInCheckbox from "./SmsOptInCheckbox";
import CommunicationPreferencesSection from "./CommunicationPreferencesSection";
import { clientsApi, conflictChecksApi } from "../../services/api";
import ConflictCheckModal from "../conflicts/ConflictCheckModal";
import { errorMessage } from "./Toast";

interface QuickCreateClientModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (client: { _id: string; displayName: string; emails?: { label: string; email: string }[]; phones?: { label: string; number: string }[] }) => void;
}

const CLIENT_TYPE_OPTIONS = [
  { value: "individual", label: "Individual", icon: User },
  { value: "organization", label: "Organization", icon: Building2 },
  { value: "estate", label: "Estate", icon: FileText },
];

const QuickCreateClientModal: React.FC<QuickCreateClientModalProps> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ displayName: "", type: "individual", email: "", phone: "", smsOptIn: false });
  const [commPrefs, setCommPrefs] = useState<Record<string, string>>({});
  const [commPrefsOpen, setCommPrefsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState<string[]>([]);
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout>>();
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictSearchTerms, setConflictSearchTerms] = useState<Array<{ name?: string; email?: string; phone?: string }>>([]);
  const [conflictExcludeId, setConflictExcludeId] = useState<string | undefined>();

  const checkDuplicateName = useCallback((name: string) => {
    clearTimeout(dupCheckTimer.current);
    if (!name.trim() || name.trim().length < 2) {
      setDuplicateWarning([]);
      return;
    }
    dupCheckTimer.current = setTimeout(async () => {
      try {
        const res = await clientsApi.list({ search: name.trim(), limit: 5 });
        const data = (res.data as any).data || res.data || [];
        const matches = data.filter(
          (c: any) => c.displayName.toLowerCase() === name.trim().toLowerCase()
        );
        setDuplicateWarning(matches.map((c: any) => c.displayName));
      } catch {
        setDuplicateWarning([]);
      }
    }, 400);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.displayName.trim()) return;
    setCreating(true);
    setSubmitError("");
    try {
      // Build communication preferences — only include non-default values
      const communicationPreferences: Record<string, any> = {};
      Object.entries(commPrefs).forEach(([key, val]) => {
        if (val && val !== "") {
          if (key === "generalCommunications") {
            communicationPreferences[key] = val === "yes";
          } else {
            communicationPreferences[key] = val;
          }
        }
      });

      const res = await clientsApi.create({
        displayName: form.displayName.trim(),
        type: form.type,
        emails: form.email ? [{ label: "main", email: form.email }] : [],
        phones: form.phone ? [{ label: "main", number: form.phone }] : [],
        smsOptIn: form.smsOptIn,
        ...(Object.keys(communicationPreferences).length > 0 ? { communicationPreferences } : {}),
      });
      const newClient = res.data;
      onCreated({
        _id: newClient._id,
        displayName: newClient.displayName,
        emails: newClient.emails || (form.email ? [{ label: "main", email: form.email }] : []),
        phones: newClient.phones || (form.phone ? [{ label: "main", number: form.phone }] : []),
      });

      // Auto-run conflict check
      const terms: Array<{ name?: string; email?: string; phone?: string }> = [];
      const term: { name?: string; email?: string; phone?: string } = {};
      if (form.displayName.trim()) term.name = form.displayName.trim();
      if (form.email.trim()) term.email = form.email.trim();
      if (form.phone.trim()) term.phone = form.phone.trim();
      if (Object.keys(term).length > 0) terms.push(term);

      if (terms.length > 0) {
        try {
          const checkRes = await conflictChecksApi.search({
            searchTerms: terms,
            excludeClientId: newClient._id,
          });
          if (checkRes.data?.matches?.length > 0) {
            setConflictSearchTerms(terms);
            setConflictExcludeId(newClient._id);
            setShowConflictModal(true);
          }
        } catch {
          // Conflict check failed silently
        }
      }

      setForm({ displayName: "", type: "individual", email: "", phone: "", smsOptIn: false });
      setCommPrefs({});
      setCommPrefsOpen(false);
      setDuplicateWarning([]);
      onClose();
    } catch (err) {
      console.error("Failed to create client:", err);
      setSubmitError(errorMessage(err, "Couldn't create the client. Please try again."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
    <PremiumModal
      open={open}
      onClose={() => { setSubmitError(""); onClose(); }}
      title="New Client"
      icon={UserPlus}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => { setSubmitError(""); onClose(); }}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="quick-create-client"
            disabled={creating || !form.displayName.trim()}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {creating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </span>
            ) : "Create Client"}
          </button>
        </div>
      }
    >
      <form id="quick-create-client" onSubmit={handleSubmit} className="space-y-5">
        {submitError && (
          <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{submitError}</span>
          </div>
        )}
        {/* Client Type */}
        <div>
          <div className="flex items-center gap-3 pt-2 mb-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Client Type</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {CLIENT_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm({ ...form, type: opt.value })}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                  form.type === opt.value
                    ? "border-brand-accent bg-brand-light text-brand"
                    : "border-slate-200 hover:border-slate-300 text-slate-600"
                }`}
              >
                <opt.icon className="h-5 w-5" />
                <span className="text-sm font-medium">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Display Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Display Name *</label>
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              required
              autoFocus
              value={form.displayName}
              onChange={(e) => {
                setForm({ ...form, displayName: e.target.value });
                checkDuplicateName(e.target.value);
              }}
              className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
              placeholder="John Smith or Acme Corp"
            />
          </div>
          {duplicateWarning.length > 0 ? (
            <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                A client named <span className="font-semibold">"{duplicateWarning[0]}"</span> already exists.
                You can still create this client, but make sure it's not a duplicate.
              </p>
            </div>
          ) : (
            <p className="text-xs text-slate-400 mt-1">How this client appears across the system</p>
          )}
        </div>

        {/* Contact Information */}
        <div>
          <div className="flex items-center gap-3 pt-2 mb-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Contact Information</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <SmsOptInCheckbox
                checked={form.smsOptIn}
                onChange={(val) => setForm({ ...form, smsOptIn: val })}
              />
            </div>
          </div>
        </div>

        {/* Communication Preferences (collapsible) */}
        <CommunicationPreferencesSection
          values={commPrefs}
          onChange={setCommPrefs}
          open={commPrefsOpen}
          onToggle={() => setCommPrefsOpen(!commPrefsOpen)}
        />
      </form>
    </PremiumModal>

    {/* Post-creation conflict check modal */}
    <ConflictCheckModal
      open={showConflictModal}
      onClose={() => setShowConflictModal(false)}
      searchTerms={conflictSearchTerms}
      excludeClientId={conflictExcludeId}
      onResolved={() => setShowConflictModal(false)}
    />
    </>
  );
};

export default QuickCreateClientModal;
